import * as React from 'react';
// tslint:disable-next-line:import-name
import * as ReactDayPicker from 'react-day-picker';
// tslint:disable-next-line:no-submodule-imports
import DayPickerInput from 'react-day-picker/DayPickerInput';
// tslint:disable-next-line:no-submodule-imports
import DayPicker from 'react-day-picker/DayPicker';
// tslint:disable-next-line:no-submodule-imports
import MomentLocaleUtils from 'react-day-picker/moment';
import Styled, { ThemeContext } from 'styled-components';
import merge from 'lodash/merge';

import pad from '../_ThemeTwo_/Utils/padNumber';

import DatePickerStyles from './DatePickerStyles';

const { formatDate, parseDate } = MomentLocaleUtils;

export const DatePickerContainer = Styled.div<{ flexBasis?: string}>`
  ${DatePickerStyles}
  flex-basis: ${props => props.flexBasis ? props.flexBasis : undefined};
`;

export const usePrepDayPickerProps = (props: ReactDayPicker.DayPickerProps) => {
  /* overrides day picker props from theme with day picker props passed into component
  so the inline props on component instance take precedence */
  const { datepicker: { dayPickerProps, day: { leadingZero } } } = React.useContext(ThemeContext);

  const themedDayPickerProps: ReactDayPicker.DayPickerProps = {...dayPickerProps};

  // if theme calls for leading zeros, pass renderDay function to format
  if (leadingZero) {
    themedDayPickerProps.renderDay = (day: Date) => pad(day.getDate());
  }

  return merge({}, themedDayPickerProps, props);
};

const handleKeyDownInput: (
  e: React.KeyboardEvent<HTMLInputElement>, pickerRef: React.RefObject<ReactDayPicker.default>
) => void = ({ key }, pickerRef) => {
  if (key === 'ArrowDown' && pickerRef.current) {
    pickerRef.current.focus();
  }
};

const handleKeyDownDatePicker: (
  e: React.KeyboardEvent<HTMLDivElement>, inputRef: React.RefObject<DayPickerInput>
) => void = ({ key }, inputRef) => {
  if ((key === 'Esc' || key === 'Escape') && inputRef.current) {
    inputRef.current.hideDayPicker();
  }
};

export const DatePickerInput: React.FC<ReactDayPicker.DayPickerInputProps & { flexBasis?: string }> = props => {
  const inputRef = React.useRef<DayPickerInput>(null);
  const pickerRef = React.useRef<ReactDayPicker.default>(null);

  const { datepicker: { input: themedInput} } = React.useContext(ThemeContext);

  const dayPickerProps = usePrepDayPickerProps(props.dayPickerProps || {});

  // inline format prop -> format from theme -> fallback format
  const format = props.format || themedInput.valueFormat || 'YYYY-M-D';
  // placeholder falls back to today's date formatted by format string
  const placeholder = props.placeholder || formatDate(new Date(), format);

  return (
    <DatePickerContainer flexBasis={props.flexBasis}>
      <DayPickerInput
        {...props}
        format={format}
        placeholder={placeholder}
        // date utilities for formatting input values upon day picker selection
        formatDate={formatDate}
        parseDate={parseDate}
        dayPickerProps={{
          // Need to do this because the TS types are incorrect and are not including ref as a prop option
          // @ts-ignore
          ref: pickerRef,
          onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => handleKeyDownDatePicker(e, inputRef),
          ...dayPickerProps
        }}
        ref={inputRef}
        inputProps={{
          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDownInput(e, pickerRef),
          ...props.inputProps,
        }}
        keepFocus={false}
      />
    </DatePickerContainer>
  );
};

DatePickerInput.displayName = 'DatePickerInput';

export const DatePicker: React.FC<ReactDayPicker.DayPickerProps> = props => {
  const dayPickerProps = usePrepDayPickerProps(props || {});

  return (
    <DatePickerContainer>
      <DayPicker
        {...dayPickerProps}
      />
    </DatePickerContainer>
  );
};

DatePicker.displayName = 'DatePicker';

export { ReactDayPicker, DayPickerInput as ReactDayPickerInput };
